import React from 'react';
import { Box, TextField, Typography, Grid, Slider } from '@mui/material';
import useEmailEditorStore from '../../utilities/emailEditorStore';
import BlockProps from './common/BlockProps';
import { BLOCK_TYPES } from '../../utilities/blockTypes'; // Import block types
import { WidthWide } from '@mui/icons-material';
function ColumnBlockProperties({ blockId }) {
  const { getBlockById, updateBlockProperties, getParentBlock, getChildrenBlocks } = useEmailEditorStore();
  
  // Get the current block data from the store
  const block = getBlockById(blockId);
  if (!block) {
    return <Typography>Block not found</Typography>;
  }
  
  // Provide default properties if properties is undefined
  const properties = block.properties || {};
  
  // Generic property change handler
  const handlePropertyChange = (propertyName, value) => {
    updateBlockProperties(blockId, { [propertyName]: value });
  };
  
  const handleColumnWidthChange = (propertyName, value) => {
    // Get parent block and its children (sibling columns)
    const parentBlock = getParentBlock(blockId);
    if (!parentBlock) return;
    
    const siblingColumns = getChildrenBlocks(parentBlock.id).filter(
      child => child.type === BLOCK_TYPES.COLUMN
    );
    
    // Skip if only one column
    if (siblingColumns.length <= 1) {
      updateBlockProperties(blockId, { [propertyName]: `${value}%` });
      return;
    }
    
    // Get column gap from parent properties - handle different data types
    const columnGapRaw = parentBlock.properties?.columngap || '5px';
    const columnGap = parseFloat(
      typeof columnGapRaw === 'string' 
        ? columnGapRaw.replace('px', '') 
        : String(columnGapRaw)
    );
    
    // For email templates, we typically work with fixed pixel widths
    // Get parent container width (default email width from global properties)
    const emailWidth = 600; // You might want to get this from global properties
    
    // Calculate gap as percentage of container width
    const totalGapWidth = (siblingColumns.length - 1) * columnGap;
    const availableWidthPixels = emailWidth - totalGapWidth;
    const gapPercentage = (totalGapWidth / emailWidth) * 100;
    
    // Available width percentage after accounting for gaps
    const availableWidthPercentage = 100 - gapPercentage;
    
    // Ensure the new width doesn't exceed available width
    const maxAllowedWidth = availableWidthPercentage - (siblingColumns.length - 1) * 5; // Reserve 5% min for each other column
    const adjustedValue = Math.min(value, maxAllowedWidth);
    
    // Update current column width
    updateBlockProperties(blockId, { [propertyName]: `${adjustedValue}%` });
    
    // Calculate remaining width for other columns
    const remainingWidth = availableWidthPercentage - adjustedValue;
    const otherColumns = siblingColumns.filter(col => col.id !== blockId);
    
    if (otherColumns.length > 0) {
      // Get current widths of other columns
      const currentOtherWidths = otherColumns.map(col => 
        parseFloat(col.properties?.width?.replace('%', '') || '20')
      );
      const currentOtherTotal = currentOtherWidths.reduce((sum, w) => sum + w, 0);
      
      // Distribute remaining width proportionally among other columns
      otherColumns.forEach((col, index) => {
        let newWidth;
        
        if (currentOtherTotal > 0) {
          // Proportional distribution based on current widths
          const proportion = currentOtherWidths[index] / currentOtherTotal;
          newWidth = remainingWidth * proportion;
        } else {
          // Equal distribution if all other columns have 0 width
          newWidth = remainingWidth / otherColumns.length;
        }
        
        // Ensure minimum width of 5%
        newWidth = Math.max(5, Math.min(newWidth, availableWidthPercentage - 5));
        
        updateBlockProperties(col.id, { width: `${Math.round(newWidth * 100) / 100}%` });
      });
    }
  };
  
  return (
    <>
      <Box>
        <Typography variant="body2" gutterBottom>
          Column Width
        </Typography>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid>
            <WidthWide />
          </Grid>
          <Grid size="grow">
            <Slider
              value={parseInt(properties.width?.replace('%', '') || '5', 10)}
              onChange={(e, value) => handleColumnWidthChange('width', value)}
              aria-labelledby="input-slider-columnwidth"
              step={1}
              marks
              max={100}
              min={5}
            />
          </Grid>
          <Grid>
            <Typography variant="body2" gutterBottom>
              {properties.width}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <BlockProps properties={properties} onPropertyChange={handlePropertyChange} />
    </>
  );
}

export default ColumnBlockProperties;